import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
	ArrowDownAZ,
	ArrowUpAZ,
	ChevronDown,
	CloudDownload,
	RefreshCw,
	Search,
	SearchX,
	Settings,
	Upload,
	Video,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSyncTranscripts } from "@/hooks/transcripts/useTranscriptMutations";
import { useTranscriptsUI } from "./TranscriptsProvider";
import { useFilteredTranscripts } from "./useFilteredTranscripts";
import { TranscriptListItem } from "./TranscriptListItem";
import type { TranscriptSortField } from "./utils";
import type { TranscriptListItem as TranscriptListItemData } from "@/types/transcript";

const SORT_FIELDS: TranscriptSortField[] = [
	"date",
	"subject",
	"organizer",
	"participants",
	"duration",
	"linkedCandidates",
];

const ListSkeleton: React.FC = () => (
	<div className="space-y-1 p-3" aria-hidden>
		{Array.from({ length: 7 }).map((_, index) => (
			<div key={index} className="flex items-center gap-3 px-1 py-2">
				<Skeleton className="h-10 w-10 shrink-0 rounded-sm" />
				<div className="flex-1 space-y-2">
					<div className="flex justify-between gap-4">
						<Skeleton className="h-3.5 w-2/5" />
						<Skeleton className="h-3 w-10" />
					</div>
					<Skeleton className="h-3 w-4/5" />
				</div>
			</div>
		))}
	</div>
);

export interface TranscriptListProps {
	transcripts: TranscriptListItemData[] | undefined;
	isLoading: boolean;
	isError: boolean;
	isFetching: boolean;
	refetch: () => void;
	onUpload: () => void;
	/** Org-wide Teams sync + sort controls; hidden on the embedded candidate tab. */
	showSync?: boolean;
	showSort?: boolean;
	className?: string;
}

/** Left pane: search, sync/upload actions, sorting and the transcript list. */
export const TranscriptList: React.FC<TranscriptListProps> = ({
	transcripts,
	isLoading,
	isError,
	isFetching,
	refetch,
	onUpload,
	showSync = true,
	showSort = true,
	className,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { selectedId, setSelectedId, search, setSearch, sortField, setSortField, sortOrder, setSortOrder } =
		useTranscriptsUI();
	const syncMutation = useSyncTranscripts();
	const filtered = useFilteredTranscripts(transcripts);

	const hasTranscripts = (transcripts?.length || 0) > 0;

	const sortLabel = (field: TranscriptSortField) =>
		t(`transcripts.fields.${field}`, {
			date: "Date",
			subject: "Subject",
			organizer: "Organizer",
			participants: "Participants",
			duration: "Duration",
			linkedCandidates: "Linked candidates",
		}[field]);

	return (
		<div className={cn("flex h-full min-h-0 flex-col bg-background", className)}>
			<div className="shrink-0 space-y-2 border-b border-border p-3">
				<div className="flex items-center gap-2">
					<div className="relative flex-1">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							placeholder={t("transcripts.search", "Search transcripts…")}
							className="h-9 pl-8"
							aria-label={t("transcripts.search", "Search transcripts…")}
						/>
					</div>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-9 w-9 shrink-0 text-muted-foreground"
								onClick={() => refetch()}
								aria-label={t("transcripts.refresh", "Refresh")}
							>
								<RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">{t("transcripts.refresh", "Refresh")}</TooltipContent>
					</Tooltip>
					{showSync && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-9 w-9 shrink-0 text-muted-foreground"
									onClick={() => syncMutation.mutate()}
									disabled={syncMutation.isPending}
									aria-label={t("transcripts.sync", "Sync from Microsoft Teams")}
								>
									<CloudDownload className={cn("h-4 w-4", syncMutation.isPending && "animate-spin")} />
								</Button>
							</TooltipTrigger>
							<TooltipContent side="bottom">{t("transcripts.sync", "Sync from Microsoft Teams")}</TooltipContent>
						</Tooltip>
					)}
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-9 w-9 shrink-0 text-muted-foreground"
								onClick={onUpload}
								aria-label={t("transcripts.upload", "Upload transcript")}
							>
								<Upload className="h-4 w-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">{t("transcripts.upload", "Upload transcript")}</TooltipContent>
					</Tooltip>
				</div>
				{showSort && (
					<div className="flex items-center gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
									{t("sortBar.sortBy", "Sort by")}: {sortLabel(sortField)}
									<ChevronDown className="h-3.5 w-3.5 opacity-60" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start">
								{SORT_FIELDS.map((field) => (
									<DropdownMenuItem
										key={field}
										onClick={() => setSortField(field)}
										className={cn(field === sortField && "bg-accent")}
									>
										{sortLabel(field)}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 text-muted-foreground"
									onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
									aria-label={
										sortOrder === "asc"
											? t("sortBar.ascending", "Ascending")
											: t("sortBar.descending", "Descending")
									}
								>
									{sortOrder === "asc" ? <ArrowUpAZ className="h-4 w-4" /> : <ArrowDownAZ className="h-4 w-4" />}
								</Button>
							</TooltipTrigger>
							<TooltipContent side="bottom">
								{sortOrder === "asc"
									? t("sortBar.ascending", "Ascending")
									: t("sortBar.descending", "Descending")}
							</TooltipContent>
						</Tooltip>
					</div>
				)}
			</div>

			<div className="relative min-h-0 flex-1">
				<div className="absolute inset-0 overflow-y-auto scrollbar-thin">
					{isLoading ? (
						<ListSkeleton />
					) : isError ? (
						<div className="flex h-full flex-col items-center justify-center p-6">
							<EmptyState
								icon={Video}
								title={t("transcripts.errors.loadTitle", "Couldn't load transcripts")}
								description={t("transcripts.errors.loadDescription", "Something went wrong while loading transcripts.")}
							/>
							<Button variant="outline" size="sm" className="mt-2 gap-2" onClick={() => refetch()}>
								<RefreshCw className="h-4 w-4" />
								{t("transcripts.errors.retry", "Retry")}
							</Button>
						</div>
					) : filtered.length === 0 ? (
						hasTranscripts ? (
							<EmptyState
								icon={SearchX}
								title={t("transcripts.empty.noResultsTitle", "No matching transcripts")}
								description={t("transcripts.empty.noResultsDescription", "Try a different search.")}
								className="h-full"
							/>
						) : (
							<div className="flex h-full flex-col items-center justify-center p-6">
								<EmptyState
									icon={Video}
									title={t("transcripts.empty.noneTitle", "No transcripts yet")}
									description={t(
										"transcripts.empty.noneDescription",
										"Sync your Microsoft Teams meetings or upload a transcript to get started."
									)}
								/>
								{showSync && (
									<div className="mt-2 flex flex-wrap items-center justify-center gap-2">
										<Button
											variant="outline"
											size="sm"
											className="gap-2"
											onClick={() => syncMutation.mutate()}
											disabled={syncMutation.isPending}
										>
											<CloudDownload className={cn("h-4 w-4", syncMutation.isPending && "animate-spin")} />
											{t("transcripts.empty.syncCta", "Sync from Teams")}
										</Button>
										<Button
											variant="outline"
											size="sm"
											className="gap-2"
											onClick={() => navigate("/settings?tab=integrations")}
										>
											<Settings className="h-4 w-4" />
											{t("transcripts.empty.connectCta", "Connect Microsoft")}
										</Button>
									</div>
								)}
							</div>
						)
					) : (
						<ul
							role="listbox"
							aria-label={t("transcripts.title", "Transcripts")}
							className="divide-y divide-border/60"
						>
							{filtered.map((transcript) => (
								<TranscriptListItem
									key={transcript.id}
									transcript={transcript}
									selected={transcript.id === selectedId}
									onSelect={setSelectedId}
								/>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
};
